import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import Import from './Import';
import DateField from './DateField';
import { useApp } from '../store';
import { remember } from '../trail';
import { can } from '../permissions';
import { DEFAULT_IDLE_WINDOWS, type IdleWindows } from '../idle-nudge';
import { checklistPromptsOf, type ChecklistPrompts } from '../checklist-prompts';
import Expander from './Expander';
import { SUGGESTED_CHECKS } from '../done';
import { knownDevices, lastRelease } from '../releases';
import { COPYRIGHT } from '../brand';
import { livePhases } from '../phases';
import { isInService } from '../handoff';
import { humanDay } from '../when';
import AssistantSetup from './AssistantSetup';
import { useTabKeys } from './useTabKeys';
import AreaSettings from './AreaSettings';
import LabelSettings from './LabelSettings';
import TypeSettings from './TypeSettings';
import PhaseSettings from './PhaseSettings';
import EpicSettings from './EpicSettings';
import AccountPanel from './AccountPanel';
import CurrencyRates from './CurrencyRates';
import WorkCalendarEditor from './WorkCalendarEditor';
import VersionChip from './VersionChip';
import { DEFAULT_SOURCE_NAME, SOURCE_BLURB, SOURCE_LABEL, SOURCES, TRACKER_OWNED, type IssueSource } from '../ownership';
import {
  CADENCE_BLURB,
  CADENCE_LABEL,
  DELIVERY_MODE_BLURB,
  DELIVERY_MODE_LABEL,
} from '../projects';
import { BOARD_CARD_DEFAULT_LABEL, CARD_STANCE_ORDER, type CardStance } from '../company-role';
import { initials } from '../utils';
import { offerBack } from '../signatories';
import SignoffRule from './SignoffRule';
import InfoTip from './InfoTip';
import { SIGNOFF_STEP_LABEL, SLOT_LABEL, SLOT_STEP, isPlain, readPolicy, usesSlots, usesStages } from '../signoff-policy';
import type { Signatory, SignoffStep } from '../types';
import { STATUS_LABELS } from '../types';


/**
 * A settings text box that writes once — when you leave it — not on every
 * keystroke.
 *
 * ⚠️ WHY THIS EXISTS. These fields were plain `<input onChange={setMeta…}>`,
 * and `setMeta` persists on the spot (store.ts). Since migration 0166 the
 * database records a trail entry for every settings field that moves — so each
 * keystroke of a name became its own "changed the project owner from Kevin to
 * Kevin M" line, fifteen of them to type one name. The same first-character
 * trap the signatory roster was pulled out of (see setSignatory), arriving
 * through the audit trail instead of the roster.
 *
 * So the typing is kept LOCAL and the write happens on blur (or Enter): one
 * logical edit, one database write, one trail entry. `format` runs as you type
 * (e.g. upper-casing a project key); `normalize` runs once on commit (e.g.
 * trimming a URL), where doing it per keystroke would fight the person typing.
 * The box follows the stored value whenever it is not the one being edited —
 * first load, another writer, a change of board.
 */
function MetaText({
  value,
  onCommit,
  format,
  normalize,
  ...rest
}: {
  value: string;
  onCommit: (v: string) => void;
  format?: (raw: string) => string;
  normalize?: (v: string) => string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [draft, setDraft] = useState(value);
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) setDraft(value);
  }, [value]);
  const commit = () => {
    editing.current = false;
    const next = normalize ? normalize(draft) : draft;
    if (next !== draft) setDraft(next);
    if (next !== value) onCommit(next);
  };
  return (
    <input
      {...rest}
      value={draft}
      onFocus={() => {
        editing.current = true;
      }}
      onChange={(e) => setDraft(format ? format(e.target.value) : e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        else if (e.key === 'Escape') {
          setDraft(value);
          editing.current = false;
          e.currentTarget.blur();
        }
      }}
    />
  );
}

export default function Settings() {
  const meta = useApp((s) => s.meta);
  const members = useApp((s) => s.members);
  const issues = useApp((s) => s.issues);
  const setMeta = useApp((s) => s.setMeta);
  const setNumbering = useApp((s) => s.setNumbering);
  const recordAudit = useApp((s) => s.recordAudit);
  const role = useApp((s) => s.shownRole);
  const canSettings = can(role, 'editSettings');

  /* #327 — putting a board away and bringing it back. */
  const projects = useApp((s) => s.projects);
  const archivedProjects = useApp((s) => s.archivedProjects);
  const projectId = useApp((s) => s.projectId);
  const archiveProject = useApp((s) => s.archiveProject);
  /** True once "Put it away" has been pressed and is waiting to be confirmed. */
  const [archiving, setArchiving] = useState(false);
  /** What the last attempt had to say. Empty when it worked. */
  const [archiveSays, setArchiveSays] = useState('');
  /*
   * ⚠️ THE CONFIRMATION DOES NOT SURVIVE A CHANGE OF BOARD, and it used to.
   *
   * Found by the browser check, not by reading this: Settings is not unmounted
   * when you switch board, so an open "Put Fixture away?" box was still open,
   * still saying Fixture, after switching to Throwaway — and the button under
   * it acts on whatever board is now current. A confirmation that names one
   * thing and does another is worse than no confirmation at all.
   *
   * The refusal goes with it for the same reason: it was about a board nobody
   * is looking at any more.
   */
  useEffect(() => {
    setArchiving(false);
    setArchiveSays('');
  }, [projectId]);

  /* Half an owner is not a person yet — the same rule the panel uses, so the
     row does not appear halfway through typing a name. */
  const ownerNamed =
    (meta.projectOwnerName ?? '').trim() !== '' && (meta.projectOwnerPosition ?? '').trim() !== '';

  /* THE STEPS A NAMED PERSON CAN BE TAGGED TO, and what to call them.
     On a slots board that is the two slots which take named people — the tester
     and the approver — labelled as slots. On the older rank board it is the
     chain the rule below has switched on, or nothing on a board that approves in
     one go. Either way the panel never offers a step this board does not use. */
  const slotsOn = usesSlots(readPolicy(meta.signoffPolicy));
  /* On the stage model, people are named in the stages themselves, so the
     hand-typed signatories panel (a legacy-model surface) is not shown. */
  const stagesOn = usesStages(readPolicy(meta.signoffPolicy));
  const chain: SignoffStep[] = (() => {
    if (slotsOn) return [SLOT_STEP.uat, SLOT_STEP.approver];
    const policy = { ...readPolicy(meta.signoffPolicy), named: [] };
    return isPlain(policy) ? [] : policy.steps.map((s) => s.step);
  })();
  const stepLabel = (s: SignoffStep): string =>
    slotsOn
      ? s === SLOT_STEP.uat
        ? SLOT_LABEL.uat
        : s === SLOT_STEP.approver
          ? SLOT_LABEL.approver
          : SIGNOFF_STEP_LABEL[s]
      : SIGNOFF_STEP_LABEL[s];

  /**
   * THE PANEL, AND NOTHING ELSE.
   *
   * This used to add everybody on the panel to the remembered roster on the
   * same keystroke, reasoning that waiting for the screen to be left is waiting
   * for a moment that may never come. That was wrong in a way a screenshot made
   * obvious: "complete" only means both boxes have something in them, which is
   * true after the FIRST CHARACTER of the position. So typing "Executive
   * Director" recorded thirteen people — "E", "Ex", "Exe" — and one abandoned
   * row left several dozen more. The board filled with names nobody had.
   *
   * Somebody is remembered when they are actually ASKED TO SIGN — see
   * requestSignoff and sendDocumentForSignature. That is what the roster claims
   * on its face ("Signed here before"), and it is a moment that does arrive.
   */
  const setSignatory = (at: number, next: Signatory | null) => {
    const now = [...(meta.signatories ?? [])];
    if (next) now[at] = next;
    else now.splice(at, 1);
    /*
     * ONE APPROVER, AND TAGGING A SECOND MOVES THE TAG — Vinnie, 6 September
     * 2026: *"not all IT managers have to sign, just 1"*. The Approver slot is a
     * single named person, so tagging somebody to it takes the tag off whoever
     * held it — the sheet does not quietly grow a second approver. Only on a
     * slots board; the rank rule can ask a whole rank at a step and this would
     * be wrong there.
     */
    if (slotsOn && next && (next.step ?? '') === SLOT_STEP.approver)
      for (let i = 0; i < now.length; i++)
        if (i !== at && (now[i].step ?? '') === SLOT_STEP.approver)
          now[i] = { ...now[i], step: '' as SignoffStep };
    setMeta({ signatories: now });
  };
  /** Held while the date is being typed — see the field below. */
  const [importing, setImporting] = useState(false);
  const openSetup = useApp((s) => s.openSetup);
  const source: IssueSource = meta.source ?? 'tracker';

  /* How this board labels its issues. A prefix that is empty or a bare '#' both
     mean plain numbering — see setNumbering. The key writes the prefix, so the
     key-prefix choice is only offered once a key exists. */
  const numKey = (meta.key || '').trim();
  const usesKey = !!(meta.refPrefix && meta.refPrefix !== '#');
  const sprints = useApp((s) => s.sprints);
  const setCadence = useApp((s) => s.setCadence);
  const setDeliveryMode = useApp((s) => s.setDeliveryMode);
  const setCardDefaults = useApp((s) => s.setCardDefaults);
  // The go-live -> in-service handoff + its undo (#435 surface 2). A board in service
  // is read from its frozen go-live, not its mode, so reverting needs its own action
  // (clears the go-live, keeps the delivery record) rather than the plain mode card.
  const revertFromService = useApp((s) => s.revertFromService);
  const inService = isInService(meta);
  // Absent is a project — see ProjectMeta.deliveryMode and src/lifecycle.ts.
  const deliveryMode = meta.deliveryMode ?? 'project';
  const cardDefaults = meta.cardDefaults ?? 'follow';
  // Strict separation of duties for scope changes (#435 A). Absent = soft (a
  // stand-in is documented, not blocked).
  const setRequireSeparateScopeApprover = useApp((s) => s.setRequireSeparateScopeApprover);
  const strictScopeApprover = meta.requireSeparateScopeApprover ?? false;
  // The delivery nudge (#483) — warn when a payment is approved below a chosen share
  // of its work being delivered. Off by default; the bar is a percentage.
  const setDeliveryNudge = useApp((s) => s.setDeliveryNudge);
  // Switching to a continuous service HIDES a live plan and the Finances group, so
  // the switch asks first when there is something to hide (R15, #435 C).
  const deliveryPhases = useApp((s) => s.phases);
  const activeProject = useApp((s) => s.projects.find((p) => p.id === s.projectId));
  const hasPlanOrBudget =
    livePhases(deliveryPhases).length > 0 || activeProject?.budget != null || activeProject?.contractValue != null;
  const [confirmContinuous, setConfirmContinuous] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  // The optional In Staging column (#471) — a per-board opt-in, like delivery mode.
  // Turning it ON just reveals the column. Turning it OFF while cards sit in staging
  // is guided: the person picks where those cards go, and the toggle will not flip
  // until they have — nothing is stranded in a column the board no longer shows.
  const setStagingEnabled = useApp((s) => s.setStagingEnabled);
  // Companion to In Staging (0183): with the column on, hide Done so the board
  // fits without scrolling. Only the board column is hidden — a card is still
  // marked done from its own picker, and Done returns when In Staging is off.
  const setHideDoneInStaging = useApp((s) => s.setHideDoneInStaging);
  const hideDoneOn = meta.hideDoneInStaging === true;
  const [hideDoneBusy, setHideDoneBusy] = useState(false);
  // Show the backlog as a column on the board (0218) — a per-board opt-in like In Staging.
  const setBacklogOnBoard = useApp((s) => s.setBacklogOnBoard);
  const backlogOnBoardOn = meta.backlogOnBoard === true;
  const [backlogOnBoardBusy, setBacklogOnBoardBusy] = useState(false);
  const moveIssues = useApp((s) => s.moveIssues);
  // Hard finish-to-start dependency block (0202) — a workflow rule, admin-only.
  const setDepHardBlock = useApp((s) => s.setDepHardBlock);
  const depHardBlock = meta.depHardBlock ?? false;
  const [depHardBusy, setDepHardBusy] = useState(false);
  // The board's adaptive assists (0205) — the card size/date assists and the
  // Insights Data quality panel. Admin-only to flip (configureAssists), a rung
  // above the manager who flips the ordinary board controls; both default on.
  const canConfigureAssists = can(role, 'configureAssists');
  const setCardAssists = useApp((s) => s.setCardAssists);
  const setDataQualityPanel = useApp((s) => s.setDataQualityPanel);
  const setShowOddsToEveryone = useApp((s) => s.setShowOddsToEveryone);
  const setIdleWindows = useApp((s) => s.setIdleWindows);
  const cardAssistsOn = meta.cardAssists !== false;
  const dataQualityPanelOn = meta.dataQualityPanel !== false;
  // Who sees the completion odds (0220). Off (the default, absent) keeps them a
  // Project-Manager-and-above read; on opens them to everyone on the board.
  const showOddsToEveryoneOn = meta.showOddsToEveryone === true;
  // This board's idle-nudge windows (0213) — null/absent falls back to the app
  // defaults. A board override is a plain positive-integer count of working days.
  const idleWindows = meta.idleWindows ?? DEFAULT_IDLE_WINDOWS;
  const idleCustom = meta.idleWindows != null;
  // A window is a whole number of working days, between one and a sane ceiling
  // (90 working days ≈ a third of a year — past that a nudge is meaningless).
  // Anything outside that snaps back to the value it had, or clamps to the
  // ceiling. (MetaText normalises the box to this on blur too.)
  const cleanDays = (raw: string, fallback: number): number => {
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n) || n < 1) return fallback;
    return Math.min(n, 90);
  };
  // Commit one window on blur, carried on top of the board's current windows — so
  // changing Small leaves Medium, Large and No size exactly as they were.
  const commitIdleWindow = async (key: 's' | 'm' | 'l' | 'noSize', raw: string) => {
    const base = meta.idleWindows ?? DEFAULT_IDLE_WINDOWS;
    const next: IdleWindows =
      key === 'noSize'
        ? { bySize: { ...base.bySize }, noSize: cleanDays(raw, base.noSize) }
        : { bySize: { ...base.bySize, [key]: cleanDays(raw, base.bySize[key]) }, noSize: base.noSize };
    // A blur that changed nothing (re-typed the same number, or an out-of-range value
    // that snapped back) writes nothing and says nothing — only a real change saves
    // and confirms it, so the toast means "that landed", not "you clicked away".
    const changed = key === 'noSize' ? next.noSize !== base.noSize : next.bySize[key] !== base.bySize[key];
    if (!changed) return;
    await setIdleWindows(next);
    showFlash('Saved');
  };
  const [cardAssistsBusy, setCardAssistsBusy] = useState(false);
  const [dataQualityBusy, setDataQualityBusy] = useState(false);
  const [oddsVisibilityBusy, setOddsVisibilityBusy] = useState(false);
  // This board's checklist prompts (0214) — whether a card's own checklist may offer
  // to move it, and which offers. Null/absent falls back to the app defaults (all on).
  // Admin-only like the assists above; the master switch gates the two per-kind flags.
  const setChecklistPrompts = useApp((s) => s.setChecklistPrompts);
  const showFlash = useApp((s) => s.showFlash);
  const prompts = checklistPromptsOf(meta.checklistPrompts);
  const [promptsBusy, setPromptsBusy] = useState(false);
  // Save one change on top of the board's current prompts, and confirm it landed —
  // the master switch and each kind write through the same path so none can diverge.
  const commitPrompts = async (next: ChecklistPrompts) => {
    setPromptsBusy(true);
    try {
      await setChecklistPrompts(next);
      showFlash('Saved');
    } finally {
      setPromptsBusy(false);
    }
  };
  const stagingOn = meta.stagingEnabled === true;
  const stagingCards = issues.filter((i) => i.status === 'staging');
  const [confirmStagingOff, setConfirmStagingOff] = useState(false);
  const [stagingDest, setStagingDest] = useState<'inprogress' | 'inreview' | null>(null);
  const [stagingBusy, setStagingBusy] = useState(false);
  const deliveryNudge = activeProject?.deliveryNudge;
  const nudgeOn = deliveryNudge?.on ?? false;
  const nudgePct = deliveryNudge?.pct ?? 80;
  const [nudgeDraft, setNudgeDraft] = useState(String(nudgePct));
  const [nudgeErr, setNudgeErr] = useState('');
  // Keep the draft in step with the stored bar when the board or its config changes.
  useEffect(() => { setNudgeDraft(String(nudgePct)); setNudgeErr(''); }, [nudgePct, projectId]);
  const nudgeDraftNum = Math.round(Number(nudgeDraft));
  const nudgeDraftValid = Number.isFinite(nudgeDraftNum) && nudgeDraftNum >= 1 && nudgeDraftNum <= 100;
  const nudgeDirty = nudgeOn && nudgeDraftValid && nudgeDraftNum !== nudgePct;
  const toggleNudge = async () => {
    setNudgeErr('');
    const err = await setDeliveryNudge({ on: !nudgeOn, pct: nudgeDraftValid ? nudgeDraftNum : nudgePct });
    if (err) setNudgeErr(err);
  };
  const saveNudgeBar = async () => {
    if (!nudgeDraftValid) { setNudgeErr('Set the bar between 1% and 100%.'); return; }
    setNudgeErr('');
    const err = await setDeliveryNudge({ on: true, pct: nudgeDraftNum });
    if (err) setNudgeErr(err);
  };
  const setAssistantVersions = useApp((s) => s.setAssistantVersions);
  const assistantVersions = useApp((s) => s.meta.assistantVersions ?? false);
  const builds = useApp((s) => s.builds);
  // Absent is flow, and never sprints — see ProjectMeta.cadence.
  const cadence = meta.cadence ?? 'builds';
  // Off unless somebody said otherwise — migration 0024. Read the same way
  // everywhere, so a board that has never been asked behaves identically on
  // every screen rather than differently on whichever one forgot the fallback.
  const tracksDevices = meta.tracksDevices ?? false;
  const everRecorded = knownDevices(builds);

  /**
   * Settings grew past the point where scrolling finds anything. Four groups,
   * named for what somebody is looking for rather than for how the software is
   * built — "Assistants" not "Integrations", "What done means" not "Workflow".
   *
   * The account and appearance panel is phone-only and stays outside the tabs:
   * on a phone it is the only place those live, and burying it behind a tab
   * would hide the way out of a theme somebody cannot read.
   */
  /*
   * WHO SIGNS OFF IS NOT PROJECT SETUP, and it was sitting in the same panel.
   * The tab was called "Approvers"; it is "Sign-off" now, one word for the job
   * across the app — the nav inbox is "Sign-offs", this is where you set it up.
   *
   * The Project tab answers "what is this thing and where does its code live" —
   * asked once, by whoever starts the board. Who signs its work off is a
   * different question, asked by different people, and changed far more often:
   * an approver leaves, a sponsor is added for one release, a panel is corrected
   * the day before a sign-off goes out. Vinnie, 22 August 2026.
   */
  const TABS = ['Project', 'Workflow', 'Sign-off', 'Finance & payments', 'Releases & data', 'Assistants'] as const;
  type Tab = (typeof TABS)[number];
  // R29 — the key stays as-is (it drives selection and the reveal map); the tab
  // only DISPLAYS the headline-style name.
  const TAB_LABELS: Partial<Record<Tab, string>> = {
    'Sign-off': 'Sign-Off',
    'Finance & payments': 'Finance & Payments',
    'Releases & data': 'Releases & Data',
  };
  /* ── #488 · FINDING A SETTING, AND LANDING ON THE RIGHT CATEGORY ─────────────
   *
   * Settings went from one overloaded "Project" tab to five named categories
   * (plus Assistants), so a control a person remembers — the delivery nudge, the
   * scope approver — now lives on a tab they are not looking at. REVEAL_TAB says
   * where each moved control lives; a deep-link or the ⌘K palette sets `revealId`
   * (via `drillTo`), so we open on that control's tab — on the FIRST render via
   * the initial state below, and on a later change via the effect — and App.tsx
   * then flashes the `[data-reveal]` anchor. A "find a setting" box (further
   * down) jumps to any control by name. (R15 · P6.)
   */
  const revealId = useApp((s) => s.revealId);
  const drillTo = useApp((s) => s.drillTo);
  const REVEAL_TAB: Record<string, Tab> = {
    'project-name': 'Project',
    numbering: 'Project',
    branding: 'Project',
    setup: 'Project',
    source: 'Project',
    import: 'Project',
    signoff: 'Sign-off',
    'delivery-mode': 'Workflow',
    'card-defaults': 'Workflow',
    cadence: 'Workflow',
    'working-days': 'Workflow',
    'in-staging': 'Workflow',
    'backlog-on-board': 'Workflow',
    classification: 'Workflow',
    epics: 'Workflow',
    'done-checks': 'Workflow',
    sod: 'Finance & payments',
    'scope-approver': 'Finance & payments',
    'delivery-nudge': 'Finance & payments',
    currency: 'Finance & payments',
    'version-numbering': 'Releases & data',
    'ships-to-devices': 'Releases & data',
    export: 'Releases & data',
    archive: 'Releases & data',
  };
  // Open on the tab that holds the control a deep-link points at, so a jump to a
  // moved setting never flashes the wrong tab first.
  const [tab, setTab] = useState<Tab>(() => REVEAL_TAB[revealId ?? ''] ?? 'Project');

  /* The Finance & payments category is project-mode only (the standing gate): a
     continuous service has no contract, budget or payment approvals, so the money
     group is hidden in the sidebar and the settings tab goes with it. Workflow
     always shows — that is where a board is switched back to a project. */
  const visibleTabs = TABS.filter((t) => t !== 'Finance & payments' || deliveryMode === 'project');
  const tabKeys = useTabKeys(visibleTabs, tab, setTab);
  useEffect(() => {
    if (!visibleTabs.includes(tab)) setTab('Project');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMode]);

  useEffect(() => {
    if (!revealId) return;
    const t = REVEAL_TAB[revealId];
    if (t && t !== tab && visibleTabs.includes(t)) setTab(t);
    // Re-aims only when a new reveal target arrives, not on every tab click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealId]);

  /* FIND A SETTING — one box that jumps to any control by name, so a buried
     setting is still one search away (R15 · P6). Each entry names the tab it
     lives on and the `data-reveal` anchor the jump flashes; `words` are the
     terms a person is likely to type, not the heading. */
  const [find, setFind] = useState('');
  const findRef = useRef<HTMLDivElement>(null);
  const SETTING_INDEX: { label: string; tab: Tab; reveal: string; words: string }[] = [
    { label: 'Project name, key and repository', tab: 'Project', reveal: 'project-name', words: 'name key prefix code repo repository github url identity' },
    { label: 'How issues are numbered', tab: 'Project', reveal: 'numbering', words: 'number numbering prefix key plain label' },
    { label: 'Branding — the logo on documents', tab: 'Project', reveal: 'branding', words: 'logo brand branding image document letterhead client' },
    { label: 'Set this project up', tab: 'Project', reveal: 'setup', words: 'setup set up questions wizard new project onboarding' },
    { label: 'Where this project’s issues come from', tab: 'Project', reveal: 'source', words: 'source tracker origin truth where issues come from push' },
    { label: 'Bring a backlog in from a spreadsheet', tab: 'Project', reveal: 'import', words: 'import spreadsheet csv backlog upload file' },
    { label: 'Who signs this board’s work off', tab: 'Sign-off', reveal: 'signoff', words: 'sign-off signoff approver signatory who approves owner sponsor stages signatories' },
    { label: 'How this board is run — project or continuous', tab: 'Workflow', reveal: 'delivery-mode', words: 'project continuous service delivery mode run bounded ongoing' },
    { label: 'Card Defaults — how a new card auto-assigns its creator', tab: 'Workflow', reveal: 'card-defaults', words: 'card default assign assignee accountable responsible doer creator company role vendor auto self-assign' },
    { label: 'How this board plans its work — flow or sprints', tab: 'Workflow', reveal: 'cadence', words: 'flow sprints cadence plan iteration timebox' },
    { label: 'Working Days & Holidays — the days you work and close', tab: 'Workflow', reveal: 'working-days', words: 'holiday holidays working days weekend bank holiday public holiday shutdown closure close calendar carnival christmas easter non-working forecast skip import country' },
    { label: 'An In Staging column', tab: 'Workflow', reveal: 'in-staging', words: 'staging stage column deploy test qa environment pre-prod preprod lane board column review done hide finished scroll fifth' },
    { label: 'Show the backlog on the board', tab: 'Workflow', reveal: 'backlog-on-board', words: 'backlog queue column board show hide holding pen unstarted triage pull onto board open items not counted' },
    { label: 'Starting work that waits on another card', tab: 'Workflow', reveal: 'dep-hard-block', words: 'dependency depends predecessor precedence finish to start block hard soft gate waiting on blocker start early override cannot start' },
    { label: 'Phases, areas, epics and types', tab: 'Workflow', reveal: 'classification', words: 'phase area type classify category discipline taxonomy' },
    { label: 'Epics — rename or remove', tab: 'Workflow', reveal: 'epics', words: 'epic epics rename delete remove merge tidy group initiative body of work theme endless list clean up' },
    { label: 'Labels', tab: 'Workflow', reveal: 'classification', words: 'label labels tag tags colour color chip cross-cutting' },
    { label: 'What “done” means here', tab: 'Workflow', reveal: 'done-checks', words: 'done definition checklist complete checks dod' },
    { label: 'Whether a checklist can move a card along', tab: 'Workflow', reveal: 'checklist-prompts', words: 'checklist prompt move card start finish done in progress first tick complete auto status offer definition of done' },
    { label: 'Separation of Duties', tab: 'Finance & payments', reveal: 'sod', words: 'separation duties accepted authorise approve conflict control exception' },
    { label: 'Changing the contract value — scope approver', tab: 'Finance & payments', reveal: 'scope-approver', words: 'contract value scope change approver price amount' },
    { label: 'Approving ahead of delivery — delivery nudge', tab: 'Finance & payments', reveal: 'delivery-nudge', words: 'delivery nudge warn ahead below bar percent approve payment' },
    { label: 'Currency & exchange rates', tab: 'Finance & payments', reveal: 'currency', words: 'currency money base reporting exchange rate fx foreign convert symbol budget rolls up dollar euro pound' },
    { label: 'Versions and build numbers', tab: 'Releases & data', reveal: 'version-numbering', words: 'version build number release assistant numbering' },
    { label: 'This project ships to devices', tab: 'Releases & data', reveal: 'ships-to-devices', words: 'device devices ships install phone laptop hardware' },
    { label: 'Export a backup', tab: 'Releases & data', reveal: 'export', words: 'export backup json download data' },
    { label: 'Putting this board away — archive', tab: 'Releases & data', reveal: 'archive', words: 'archive put away retire close board' },
  ];
  const findQ = find.trim().toLowerCase();
  const findHits = findQ
    ? SETTING_INDEX.filter((s) => {
        const hay = (s.label + ' ' + s.words).toLowerCase();
        return findQ.split(/\s+/).every((w) => hay.includes(w));
      }).slice(0, 7)
    : [];
  const jumpToSetting = (hit: { tab: Tab; reveal: string }) => {
    setFind('');
    setTab(hit.tab);
    drillTo('settings', hit.reveal);
  };
  /* Close the result list on a click away or Escape — the standing menu rule
     (R4), on mousedown so a click that lands on a result is not swallowed. Escape
     is caught at the window, so it closes the list from anywhere in the widget
     (the input or a focused result), not only while the input has focus. */
  useEffect(() => {
    if (!findQ) return;
    const onDown = (e: MouseEvent) => {
      if (findRef.current && !findRef.current.contains(e.target as Node)) setFind('');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && findRef.current?.contains(document.activeElement)) setFind('');
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [findQ]);

  // Typed here, saved when you leave the box.
  //
  // Binding the box straight to the saved value meant every keystroke was a
  // write, and nothing appeared until it came back. It also stripped blank lines
  // as you typed, so pressing Enter deleted the line before you could type on
  // it — the list could never grow past one item.
  const [checksDraft, setChecksDraft] = useState((meta.doneChecks ?? []).join('\n'));
  const editingChecks = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Follow the saved value, unless the person is part-way through typing.
    if (!editingChecks.current) setChecksDraft((meta.doneChecks ?? []).join('\n'));
  }, [meta.doneChecks]);

  // Saved shortly after typing stops, rather than when the box is left. Relying
  // on leaving it loses the work of anyone who types and then closes the tab,
  // and depends on a blur event that does not always arrive.
  const queueChecksSave = (text: string) => {
    editingChecks.current = true;
    setChecksDraft(text);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      editingChecks.current = false;
      const next = text.split('\n').map((l) => l.trim()).filter(Boolean);
      const now = meta.doneChecks ?? [];
      if (next.length === now.length && next.every((v, i) => v === now[i])) return;
      setMeta({ doneChecks: next });
    }, 600);
  };

  // Whatever is half-typed when this screen closes is still saved.
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ meta, members, issues }, null, 2)], {
      type: 'application/json',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `flightdeck-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
    /*
     * ── THE WHOLE BOARD LEAVING, WRITTEN DOWN — #305 ───────────────────────
     *
     * This is the largest single act of data leaving the product: every issue,
     * every member, and the project's settings, in one file, onto somebody's
     * machine, where nothing FlightDeck does can reach it again. It was the one
     * export with no record of any kind — and "who took a copy of the board,
     * and when" is the first question asked after anything goes wrong.
     *
     * The count is named because the file is otherwise a date and a size. A
     * month later, 6 issues and 300 are the same sentence without it.
     *
     * Fired and forgotten, and its refusal swallowed: the file has already been
     * handed over by the line above, so failing here can neither prevent that
     * nor be acted on by the person who pressed the button.
     */
    /*
     * ⚠️ THE REASON GOES SOMEWHERE, and swallowing it was my own fault.
     *
     * This was written `.catch(() => {})`. Vinnie pressed Export on 29 August,
     * no line appeared in History, and there was nothing anywhere to say
     * whether the write had been refused or never attempted — which is the
     * exact silence [[#289]] was raised to remove, reintroduced by me in the
     * card that was supposed to be closing gaps like it.
     *
     * The file has already been handed over by the line above, so this must
     * not fail the export and must not interrupt anybody. It goes into the
     * trail `problems` and the broken-screen report both read, the same way a
     * device that could not be written down does.
     */
    void recordAudit(
      'project',
      `downloaded the whole board as a file — ${issues.length} issue${issues.length === 1 ? '' : 's'} and ${members.length} ${members.length === 1 ? 'person' : 'people'}`
    ).catch((e: unknown) =>
      remember(
        'broke',
        `the board was downloaded and History could not be told — ${(e as { message?: string })?.message || String(e)}`
      )
    );
  };

  return (
    <div className="settings">
      {/* On a phone the sidebar is a row of tabs, so appearance, account and
          version live here instead — the only place with room for them. */}
      <section className="panel phone-only">
        <h2>You</h2>
        <AccountPanel />
        {/* On a phone the wordmark (and its version chip) is not on screen, so the
            build number lives here beside appearance — the one place with room. */}
        <div className="settings-version"><VersionChip /></div>
      </section>

      <div className="tabs" role="tablist" onKeyDown={tabKeys.onKeyDown}>
        {visibleTabs.map((name) => (
          <button
            key={name}
            role="tab"
            aria-selected={tab === name}
            ref={tabKeys.setRef(name)}
            tabIndex={tabKeys.tabIndex(name)}
            className={`tab ${tab === name ? 'is-on' : ''}`}
            onClick={() => setTab(name)}
          >
            {TAB_LABELS[name] ?? name}
          </button>
        ))}
      </div>

      {/* #488 — one box that jumps to any control by name. It sits under the
          category tabs so the tabs are the first thing read and the search the
          fallback for "I know what I want, not which tab it is on" (R15). */}
      <div className="set-find" ref={findRef}>
        <svg className="set-find-i" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <line x1="10.5" y1="10.5" x2="14" y2="14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <input
          className="input set-find-in"
          type="search"
          value={find}
          placeholder={'Find a setting — e.g. “contract value”, “phases”, “export”'}
          aria-label="Find a setting"
          onChange={(e) => setFind(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setFind('');
            if (e.key === 'Enter' && findHits.length > 0) jumpToSetting(findHits[0]);
          }}
        />
        {findHits.length > 0 && (
          <ul className="set-find-hits" role="listbox" aria-label="Matching settings">
            {findHits.map((h) => (
              <li key={h.reveal}>
                <button className="set-find-hit" role="option" aria-selected="false" onClick={() => jumpToSetting(h)}>
                  <span className="set-find-hit-l">{h.label}</span>
                  <span className="set-find-hit-c">{h.tab}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {findQ && findHits.length === 0 && (
          <div className="set-find-none">No setting matches &ldquo;{find.trim()}&rdquo;.</div>
        )}
      </div>

      {/* THE LEAD CARD — the quick way through setup, above the grouped detail.
          Shown only to someone who can edit settings; a viewer starts at Identity. */}
      {tab === 'Project' && canSettings && (
        <section className="panel set-lead" data-reveal="setup">
          <div className="set-lead-main">
            <h2>Set this project up</h2>
            <p className="hint">
              The short set of questions the board asks a new project — what it is called, where
              its issues come from, what &ldquo;done&rdquo; means, and who else has access. A quicker
              way through the screens in this and the other categories.
            </p>
          </div>
          <button className="btn set-lead-btn" onClick={openSetup}>
            Go through the questions
          </button>
        </section>
      )}

      {tab === 'Project' && <h3 className="grp-heading">Identity</h3>}

      {tab === 'Project' && (
      <section className="panel">
        <h2>Project</h2>
        <label className="stack">
          {/* Label and asterisk in one span so the flex-column `.stack` keeps
              them on one line — a bare text node + the span are two flex items
              and the * drops below (Vinnie, 13 Sep). */}
          <span>Project name <span className="req" aria-hidden="true">*</span></span>
          <MetaText
            className="input"
            data-reveal="project-name"
            aria-required="true"
            value={meta.name ?? ''}
            disabled={!canSettings}
            onCommit={(v) => setMeta({ name: v })}
          />
        </label>
        <label className="stack">
          <span>Project key <span className="req" aria-hidden="true">*</span></span>
          <span className="hint">(short prefix, e.g. ORB)</span>
          <MetaText
            className="input key-input"
            aria-required="true"
            value={meta.key ?? ''}
            disabled={!canSettings}
            format={(v) => v.toUpperCase().slice(0, 5)}
            onCommit={(v) => setMeta({ key: v })}
          />
        </label>

        {/* This was read by the issue screen to turn a commit into a link, and
            could not be set anywhere at all — so on every board where nobody had
            written it straight into the database, every commit link was dead. */}
        <label className="stack">
          Where the code lives{' '}
          <span className="hint">(so a commit on an issue becomes a link — leave empty for none)</span>
          <MetaText
            className="input"
            value={meta.repoUrl ?? ''}
            disabled={!canSettings}
            placeholder="https://github.com/owner/repo"
            normalize={(v) => v.trim().replace(/\/+$/, '')}
            onCommit={(v) => setMeta({ repoUrl: v })}
          />
        </label>

      </section>

      )}

      {tab === 'Project' && (
      <section className="panel">
        <h2 data-reveal="numbering">How issues are numbered</h2>
        <p className="hint">
          How every issue on this board is labelled &mdash; on its card, in search, and anywhere it
          is mentioned. Built from the project key above.
        </p>
        <div className="mode-cards">
          <button
            className={`mode-card ${!usesKey ? 'is-on' : ''}`}
            disabled={!canSettings}
            onClick={() => setNumbering(false)}
          >
            <span className="mode-card-title">
              Plain numbers
              {!usesKey && <span className="person-you">in use</span>}
            </span>
            <span className="mode-card-blurb">Issues are numbered #1, #2, #3.</span>
            <span className="mode-card-eg">Looks like <span className="count">#267</span></span>
          </button>
          <button
            className={`mode-card ${usesKey ? 'is-on' : ''}`}
            disabled={!canSettings || !numKey}
            onClick={() => setNumbering(true)}
          >
            <span className="mode-card-title">
              Project key
              {usesKey && <span className="person-you">in use</span>}
            </span>
            <span className="mode-card-blurb">
              Issues carry the project key, so a number is unmistakable in an email or across boards.
            </span>
            <span className="mode-card-eg">Looks like <span className="count">{numKey || 'KEY'}-267</span></span>
          </button>
        </div>
        <p className="hint">
          Changing this re-labels the issues already on the board so it keeps one scheme &mdash;{' '}
          <b>{numKey || 'KEY'}-267</b> becomes <b>#267</b>, or the reverse. The number itself never changes.
        </p>
      </section>
      )}

      {/* THE LOGO SITS WITH THE PROJECT, NOT WITH THE APPROVERS — Vinnie, 23
          August 2026: *"why is the Logo upload under approvers? It should be
          under project"*. It was put beside the approvers because both end up
          on the same produced document, which is a reason about where the thing
          is USED and not about what it IS. A logo is what this project is
          called in pictures, and it belongs next to what it is called in words. */}
      {tab === 'Project' && (
      <section className="panel">
        <h2 data-reveal="branding">Branding</h2>
        <p className="hint">
          The logo on documents this board produces. It is the client&rsquo;s mark rather than
          yours &mdash; a change request goes into their file, not ours &mdash; so it belongs to
          the board and not to the whole workspace.
        </p>
        <BoardLogo />
      </section>
      )}

      {tab === 'Project' && <h3 className="grp-heading">Where work comes from</h3>}

      {tab === 'Project' && (
      <section className="panel">
        <h2 data-reveal="source">Where this project&apos;s issues come from</h2>
        <p className="hint">
          This decides what the board may edit. It describes how a team works, so it rarely
          changes.
        </p>
        <div className="mode-cards">
          {SOURCES.map((src) => (
            <button
              key={src}
              className={`mode-card ${source === src ? 'is-on' : ''}`}
              disabled={!canSettings}
              onClick={() => setMeta({ source: src })}
            >
              <span className="mode-card-title">
                {SOURCE_LABEL[src]}
                {source === src && <span className="person-you">in use</span>}
              </span>
              <span className="mode-card-blurb">{SOURCE_BLURB[src]}</span>
            </button>
          ))}
        </div>

        {source === 'tracker' && (
          <>
            <label className="stack">
              What to call it <span className="hint">(shown wherever the board explains where something came from)</span>
              <MetaText
                className="input"
                value={meta.sourceName ?? ''}
                disabled={!canSettings}
                placeholder={DEFAULT_SOURCE_NAME}
                onCommit={(v) => setMeta({ sourceName: v })}
              />
            </label>
            <p className="hint">
              These fields belong to the tracker and are read-only here. Every push overwrites
              them, so an edit made on the board would be undone without anyone being told.
            </p>
            <div className="owned-list">
              {TRACKER_OWNED.map((f) => (
                <span key={f} className="epic-chip">
                  {f}
                </span>
              ))}
            </div>
            <p className="hint">
              Everything else belongs to the board and survives every push — comments, assignee,
              and how a blocker is classified.
            </p>
          </>
        )}
      </section>

      )}

      {tab === 'Project' && (
      <section className="panel">
        <h2>Bring a backlog in from a spreadsheet</h2>
        <p className="hint">
          If your work is already written down somewhere — a spreadsheet, or a board you can export
          one from — you do not have to type it again. You will be shown what FlightDeck made of it
          and can correct it before anything is added.
        </p>
        <button className="btn" data-reveal="import" disabled={!canSettings} onClick={() => setImporting(true)}>
          Choose a file…
        </button>
        {/* Once, not a link between two systems that both keep running — Vinnie,
            19 August 2026. Said here so nobody expects the file to stay in step. */}
        <p className="hint">
          It is read once. Changing the spreadsheet afterwards does not change the board.
        </p>
      </section>
      )}

      {/* ── WORKFLOW ──────────────────────────────────────────────────────────
          How work runs (delivery mode, cadence), how it is classified (phases,
          areas, types) and what finishing means — lifted together out of the old
          Project scroll into one short, grouped tab (#488). */}
      {tab === 'Workflow' && <h3 className="grp-heading">How work runs</h3>}

      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="delivery-mode">How this board is run</h2>
        <p className="hint">
          A project is a bounded piece of work with a plan behind it &mdash; phases,
          milestones and a Go-Live. A continuous service is ongoing work with no fixed
          end, like a support queue. Running a board as a continuous service hides the
          Plan tab <b>and the Finances group</b>, because there is no plan, contract
          or budget to track.
        </p>
        {inService ? (
          /* A handed-over board is read from its frozen go-live, not its mode, so it
             gets a dedicated state here with its own undo — which clears the Go-Live
             AND keeps the delivery record (decision 3), rather than the plain mode
             card which would only flip the mode and leave it reading "in service". */
          <div className="delivery-inservice">
            <p className="delivery-inservice-h">
              <span className="isv-pill"><span className="isv-dot" aria-hidden="true" /> In service</span>
              Handed over on <b>{humanDay(new Date(meta.actualGoLive as number).toISOString().slice(0, 10))}</b> and run
              as a continuous service. Its as-delivered record is kept, and logged in <b>Workspace History</b> and the steering pack.
            </p>
            {!confirmRevert ? (
              <button className="btn btn-small" disabled={!canSettings} onClick={() => setConfirmRevert(true)}>
                Move back to a project
              </button>
            ) : (
              <div className="delivery-confirm">
                <p className="delivery-confirm-h">
                  This shows the Plan tab and the Finances group again and clears the Go-Live, so the board
                  reads as <b>in delivery</b> once more. The <b>as-delivered record stays</b> — nothing about
                  the delivery is lost.
                </p>
                <div className="fd-actions">
                  <button
                    className="btn btn-primary btn-small"
                    onClick={() => { setConfirmRevert(false); void revertFromService(); }}
                  >
                    Move back to a project
                  </button>
                  <button className="btn btn-small" onClick={() => setConfirmRevert(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        ) : (
        <>
        <div className="mode-cards">
          {(['project', 'continuous'] as const).map((m) => (
            <button
              key={m}
              className={`mode-card ${deliveryMode === m ? 'is-on' : ''}`}
              disabled={!canSettings}
              onClick={() => {
                if (deliveryMode === m) return;
                // Switching TO continuous hides a live plan + the Finances group —
                // confirm first so a live budget is never hidden by a stray click
                // (R15, #435 C). Switching back to a project only reveals, so it
                // never needs a confirm.
                if (m === 'continuous' && hasPlanOrBudget) { setConfirmContinuous(true); return; }
                setConfirmContinuous(false);
                void setDeliveryMode(m);
              }}
            >
              <span className="mode-card-title">
                {DELIVERY_MODE_LABEL[m]}
                {deliveryMode === m && <span className="person-you">in use</span>}
              </span>
              <span className="mode-card-blurb">{DELIVERY_MODE_BLURB[m]}</span>
            </button>
          ))}
        </div>
        {confirmContinuous && (
          <div className="delivery-confirm">
            <p className="delivery-confirm-h">
              This board has a live plan or budget. Running it as a continuous service will{' '}
              <b>hide the Plan tab and the Finances group</b> — the plan, contract and budget stay
              recorded, but are out of view until you switch back to a project.
            </p>
            <div className="fd-actions">
              <button
                className="btn btn-primary btn-small"
                onClick={() => { setConfirmContinuous(false); void setDeliveryMode('continuous'); }}
              >
                Run as a continuous service
              </button>
              <button className="btn btn-small" onClick={() => setConfirmContinuous(false)}>Cancel</button>
            </div>
          </div>
        )}
        </>
        )}
      </section>
      )}

      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="card-defaults">Card Defaults</h2>
        <p className="hint">
          How a new card on <b>this board</b> fills its two assignee slots when someone
          creates it. The default follows each person&rsquo;s company role &mdash; a manager
          lands <b>Accountable</b>, a doer <b>Responsible</b> &mdash; with no per-card effort.
          Force the whole board to one slot for a doer-led board, such as a vendor delivery
          board. Every slot stays editable on the card afterwards.
        </p>
        <label className="stack">
          When a card is created, auto-assign the creator as
          <select
            value={cardDefaults}
            disabled={!canSettings}
            onChange={(ev) => void setCardDefaults(ev.target.value as CardStance)}
          >
            {CARD_STANCE_ORDER.map((s) => (
              <option key={s} value={s}>
                {BOARD_CARD_DEFAULT_LABEL[s]}
              </option>
            ))}
          </select>
          <span className="hint">
            Board-level override of each person&rsquo;s company-role default. Set to{' '}
            <b>Responsible</b> for a doer-led board such as a vendor delivery board.
          </span>
        </label>
      </section>
      )}

      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="in-staging">An In Staging column</h2>
        <p className="hint">
          Some teams put a change onto a staging or test environment before it is reviewed. Turn
          this on to add an <b>In Staging</b> column between In Progress and In Review &mdash; a
          place for work that is built and deployed but not yet signed off. Leave it off and the
          board keeps In Progress and In Review next to each other.
        </p>
        <div className={`feature-row ${stagingOn ? 'is-on' : ''}`}>
          <button
            className={`switch ${stagingOn ? 'on' : ''}`}
            role="switch"
            aria-checked={stagingOn}
            aria-label="Add an In Staging column"
            disabled={!canSettings || stagingBusy}
            onClick={() => {
              // Turning it ON only reveals the column — never needs a confirm. Turning
              // it OFF while cards sit in staging asks where they go first (and will
              // not flip until answered); with none there it flips straight away.
              if (!stagingOn) { setConfirmStagingOff(false); void setStagingEnabled(true); return; }
              if (stagingCards.length > 0) { setStagingDest(null); setConfirmStagingOff(true); return; }
              void setStagingEnabled(false);
            }}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Add an In Staging column</div>
            <p className="feature-what">
              {stagingOn
                ? 'The board shows an In Staging column between In Progress and In Review.'
                : 'The board has In Progress and In Review with nothing between them. Turn this on for teams that stage a change before it is reviewed.'}
            </p>
          </div>
          <span className={`feature-state ${stagingOn ? 'on' : ''}`}>{stagingOn ? 'On' : 'Off'}</span>
        </div>
        {confirmStagingOff && (
          <div className="delivery-confirm">
            <p className="delivery-confirm-h">
              {stagingCards.length} {stagingCards.length === 1 ? 'card is' : 'cards are'} in the
              In Staging column. Removing the column needs somewhere for{' '}
              {stagingCards.length === 1 ? 'it' : 'them'} to go — choose a column, then{' '}
              {stagingCards.length === 1 ? 'it is moved' : 'they are moved'} and the column is
              removed. Nothing is deleted.
            </p>
            <p className="hint">{stagingCards.map((i) => i.ref).join(', ')}</p>
            <div className="mode-cards">
              {(['inprogress', 'inreview'] as const).map((d) => (
                <button
                  key={d}
                  className={`mode-card ${stagingDest === d ? 'is-on' : ''}`}
                  disabled={stagingBusy}
                  onClick={() => setStagingDest(d)}
                >
                  <span className="mode-card-title">
                    {STATUS_LABELS[d]}
                    {stagingDest === d && <span className="person-you">chosen</span>}
                  </span>
                  <span className="mode-card-blurb">
                    {d === 'inprogress'
                      ? 'Send them back to In Progress — still being worked on.'
                      : 'Move them on to In Review — ready to be looked at.'}
                  </span>
                </button>
              ))}
            </div>
            <div className="fd-actions">
              <button
                className="btn btn-primary btn-small"
                disabled={!stagingDest || stagingBusy}
                onClick={async () => {
                  if (!stagingDest) return;
                  setStagingBusy(true);
                  try {
                    const res = await moveIssues(stagingCards.map((i) => i.id), stagingDest);
                    // Only remove the column once every card has actually left it — a
                    // refused move must not strand a card in a hidden lane.
                    if (res.refused === 0) {
                      await setStagingEnabled(false);
                      setConfirmStagingOff(false);
                      setStagingDest(null);
                    }
                  } finally {
                    setStagingBusy(false);
                  }
                }}
              >
                {stagingBusy
                  ? 'Moving…'
                  : `Move ${stagingCards.length === 1 ? 'the card' : `all ${stagingCards.length}`} and remove the column`}
              </button>
              <button
                className="btn btn-small"
                disabled={stagingBusy}
                onClick={() => { setConfirmStagingOff(false); setStagingDest(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {/* Companion option (0183): only when In Staging is on, since it is about
            what happens to the Done column once there is a fifth lane. */}
        {stagingOn && (
          <div className={`feature-row ${hideDoneOn ? 'is-on' : ''}`}>
            <button
              className={`switch ${hideDoneOn ? 'on' : ''}`}
              role="switch"
              aria-checked={hideDoneOn}
              aria-label="Hide the Done column while In Staging is on"
              disabled={!canSettings || hideDoneBusy}
              onClick={async () => {
                setHideDoneBusy(true);
                try { await setHideDoneInStaging(!hideDoneOn); } finally { setHideDoneBusy(false); }
              }}
            >
              <span className="switch-knob" />
            </button>
            <div className="feature-main">
              <div className="feature-name">Hide the Done column while In Staging is on</div>
              <p className="feature-what">
                {hideDoneOn
                  ? 'The board drops the Done column while In Staging is on, so the columns fit without scrolling sideways. A card is still marked done from its own menu, and the Done column comes back when you turn this off — or when you turn In Staging off.'
                  : 'The board keeps all five columns and scrolls sideways to reach Done. Turn this on to drop the Done column while In Staging is on, so the columns fit with no scrolling.'}
              </p>
            </div>
            <span className={`feature-state ${hideDoneOn ? 'on' : ''}`}>{hideDoneOn ? 'On' : 'Off'}</span>
          </div>
        )}
      </section>
      )}

      {/* SHOW THE BACKLOG ON THE BOARD (0218) — a per-board opt-in like In Staging. The
          backlog is a holding pen, not open work, so it is off the board by default; this
          reveals it as a muted, capped column for triage in place. */}
      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="backlog-on-board">Show the backlog on the board</h2>
        <p className="hint">
          The backlog is the holding pen for work you have not committed to yet, so it lives on
          its own <b>Backlog</b> view, stays off the board&rsquo;s columns, and is never counted
          as open work. Turn this on to also show a muted <b>Backlog</b> column on the board, to
          the left of To&nbsp;Do, for triaging the queue without leaving the board. The Backlog
          count in the board toolbar is always there either way.
        </p>
        <div className={`feature-row ${backlogOnBoardOn ? 'is-on' : ''}`}>
          <button
            className={`switch ${backlogOnBoardOn ? 'on' : ''}`}
            role="switch"
            aria-checked={backlogOnBoardOn}
            aria-label="Show the backlog on the board"
            disabled={!canSettings || backlogOnBoardBusy}
            onClick={async () => {
              setBacklogOnBoardBusy(true);
              try { await setBacklogOnBoard(!backlogOnBoardOn); } finally { setBacklogOnBoardBusy(false); }
            }}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Show the backlog on the board</div>
            <p className="feature-what">
              {backlogOnBoardOn
                ? 'The board shows a muted Backlog column to the left of To Do, capped so a long queue never floods the board. To Do is still the first open column, and the backlog is never counted as open work.'
                : 'The backlog lives on its own view, reached from the Backlog count in the board toolbar. Turn this on to also show it as a muted column on the board, for triaging the queue in place.'}
            </p>
          </div>
          <span className={`feature-state ${backlogOnBoardOn ? 'on' : ''}`}>{backlogOnBoardOn ? 'On' : 'Off'}</span>
        </div>
      </section>
      )}

      {/* HARD DEPENDENCY BLOCK (0202) — when a card's blocker is another card, that
          card is a predecessor: by default starting the dependent early only warns
          and records an override. An admin can make it a true block. A workflow rule,
          so it lives in Workflow for every board (not just projects). */}
      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="dep-hard-block">Starting work that waits on another card</h2>
        <p className="hint">
          When a card&rsquo;s blocker is another card, that card has to finish first. By default
          FlightDeck only <b>warns</b> if someone starts the waiting card early, and records the
          override. Turn this on to <b>block</b> it &mdash; the card cannot start until the work it
          waits on is finished.
          {!canConfigureAssists && ' Only an admin can change this.'}
        </p>
        <div className={`feature-row ${depHardBlock ? 'is-on' : ''}`}>
          <button
            className={`switch ${depHardBlock ? 'on' : ''}`}
            role="switch"
            aria-checked={depHardBlock}
            aria-label="Block a card from starting before the work it waits on is finished"
            disabled={!canConfigureAssists || depHardBusy}
            onClick={async () => {
              setDepHardBusy(true);
              try { await setDepHardBlock(!depHardBlock); } finally { setDepHardBusy(false); }
            }}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Block a card from starting before the work it waits on is finished</div>
            <p className="feature-what">
              {depHardBlock
                ? 'A card cannot move into a started column while a card it waits on is unfinished. There is no override.'
                : 'Starting a waiting card early is allowed but warns, and the override is recorded. Turn this on to block it outright.'}
            </p>
          </div>
          <span className={`feature-state ${depHardBlock ? 'on' : ''}`}>{depHardBlock ? 'On' : 'Off'}</span>
        </div>
      </section>
      )}

      {/* THE BOARD'S ADAPTIVE ASSISTS (0205) — the card size/date assists and the
          Insights Data quality panel. Both default on and help every board keep
          itself tidy; an admin can turn either off for a board that does not want
          it. Admin-only to flip (configureAssists), a rung above the manager who
          flips the other Workflow controls — so the switches show for everyone but
          only an admin can move them. A workflow concern, shown for every board. */}
      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="board-assists">Assists and data quality</h2>
        <p className="hint">
          FlightDeck can help a board keep itself tidy &mdash; suggesting a card&rsquo;s size and
          date from the cards most like it, and gathering the gaps your numbers lean on in one
          place. Turn either off for this board.
          {!canConfigureAssists && ' Only an admin can change these.'}
        </p>
        <div className={`feature-row ${cardAssistsOn ? 'is-on' : ''}`}>
          <button
            className={`switch ${cardAssistsOn ? 'on' : ''}`}
            role="switch"
            aria-checked={cardAssistsOn}
            aria-label="Suggest a card's size and date from similar cards"
            disabled={!canConfigureAssists || cardAssistsBusy}
            onClick={async () => {
              setCardAssistsBusy(true);
              try { await setCardAssists(!cardAssistsOn); } finally { setCardAssistsBusy(false); }
            }}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Suggest a card&rsquo;s size and date</div>
            <p className="feature-what">
              {cardAssistsOn
                ? 'A card with no size or due date gets a quiet suggestion from the cards most like it — a line under the field, one tap to accept. Never a block.'
                : 'Cards show no size or date suggestions. Turn on to let each card suggest the value most like its neighbours.'}
            </p>
          </div>
          <span className={`feature-state ${cardAssistsOn ? 'on' : ''}`}>{cardAssistsOn ? 'On' : 'Off'}</span>
        </div>
        <div className={`feature-row ${dataQualityPanelOn ? 'is-on' : ''}`}>
          <button
            className={`switch ${dataQualityPanelOn ? 'on' : ''}`}
            role="switch"
            aria-checked={dataQualityPanelOn}
            aria-label="Show the Data quality panel on Insights"
            disabled={!canConfigureAssists || dataQualityBusy}
            onClick={async () => {
              setDataQualityBusy(true);
              try { await setDataQualityPanel(!dataQualityPanelOn); } finally { setDataQualityBusy(false); }
            }}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Show the Data quality panel on Insights</div>
            <p className="feature-what">
              {dataQualityPanelOn
                ? 'Insights carries a board-wide roll-up of missing sizes and dates and cards that have gone quiet. Everyone sees it; a manager acts on the one-tap fixes.'
                : 'Insights does not show the Data quality panel. Turn on to surface the gaps your numbers lean on, with one-tap fixes for managers.'}
            </p>
          </div>
          <span className={`feature-state ${dataQualityPanelOn ? 'on' : ''}`}>{dataQualityPanelOn ? 'On' : 'Off'}</span>
        </div>
        {/* WHO SEES THE COMPLETION ODDS (0220) — the release/sprint "chance of making it" read.
            Admin-only like the two switches above; off by default, which keeps it a
            Project-Manager-and-above read. On opens it to everyone on the board. */}
        <div className={`feature-row ${showOddsToEveryoneOn ? 'is-on' : ''}`}>
          <button
            className={`switch ${showOddsToEveryoneOn ? 'on' : ''}`}
            role="switch"
            aria-checked={showOddsToEveryoneOn}
            aria-label="Show the completion odds to everyone on the board"
            disabled={!canConfigureAssists || oddsVisibilityBusy}
            onClick={async () => {
              setOddsVisibilityBusy(true);
              try { await setShowOddsToEveryone(!showOddsToEveryoneOn); } finally { setOddsVisibilityBusy(false); }
            }}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Show the completion odds to everyone</div>
            <p className="feature-what">
              {showOddsToEveryoneOn
                ? 'Everyone on the board sees a card’s chance of making the go-live and a sprint’s chance of finishing. The read never shows a number it can’t stand behind.'
                : 'Only a Project Manager and above sees a card’s chance of making the go-live and a sprint’s chance of finishing. Turn on to show everyone on the board.'}
            </p>
          </div>
          <span className={`feature-state ${showOddsToEveryoneOn ? 'on' : ''}`}>{showOddsToEveryoneOn ? 'On' : 'Off'}</span>
        </div>
        {/* PER-BOARD IDLE WINDOWS (0213) — how long a card may sit still before it is
            nudged, by size. Admin-only like the two switches above; blank/default
            boards use the app defaults until a window is set. Commits on blur. */}
        <div className="feature-row">
          <div className="feature-main">
            <div className="feature-name">Nudge a card that sits with no movement for longer than&hellip;</div>
            <p className="feature-what">
              A card in an active column that goes this many working days with nobody moving it
              gets a quiet nudge to the person doing it. A longer card is given longer; a card
              with no size set uses the last window. These are working days &mdash; weekends do
              not count.
            </p>
            <div className="nudge-bar idle-row">
              <label className="nudge-bar-l" htmlFor="idle-s">Small card</label>
              <div className="nudge-bar-field">
                <MetaText
                  id="idle-s"
                  className="input nudge-pct"
                  type="number"
                  min={1}
                  max={90}
                  inputMode="numeric"
                  disabled={!canConfigureAssists}
                  aria-label="Working days before a small card is nudged"
                  value={String(idleWindows.bySize.s)}
                  normalize={(v) => String(cleanDays(v, idleWindows.bySize.s))}
                  onCommit={(v) => commitIdleWindow('s', v)}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
            </div>
            <div className="nudge-bar idle-row">
              <label className="nudge-bar-l" htmlFor="idle-m">Medium card</label>
              <div className="nudge-bar-field">
                <MetaText
                  id="idle-m"
                  className="input nudge-pct"
                  type="number"
                  min={1}
                  max={90}
                  inputMode="numeric"
                  disabled={!canConfigureAssists}
                  aria-label="Working days before a medium card is nudged"
                  value={String(idleWindows.bySize.m)}
                  normalize={(v) => String(cleanDays(v, idleWindows.bySize.m))}
                  onCommit={(v) => commitIdleWindow('m', v)}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
            </div>
            <div className="nudge-bar idle-row">
              <label className="nudge-bar-l" htmlFor="idle-l">Large card</label>
              <div className="nudge-bar-field">
                <MetaText
                  id="idle-l"
                  className="input nudge-pct"
                  type="number"
                  min={1}
                  max={90}
                  inputMode="numeric"
                  disabled={!canConfigureAssists}
                  aria-label="Working days before a large card is nudged"
                  value={String(idleWindows.bySize.l)}
                  normalize={(v) => String(cleanDays(v, idleWindows.bySize.l))}
                  onCommit={(v) => commitIdleWindow('l', v)}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
            </div>
            <div className="nudge-bar idle-row">
              <label className="nudge-bar-l" htmlFor="idle-nosize">No size set</label>
              <div className="nudge-bar-field">
                <MetaText
                  id="idle-nosize"
                  className="input nudge-pct"
                  type="number"
                  min={1}
                  max={90}
                  inputMode="numeric"
                  disabled={!canConfigureAssists}
                  aria-label="Working days before a card with no size is nudged"
                  value={String(idleWindows.noSize)}
                  normalize={(v) => String(cleanDays(v, idleWindows.noSize))}
                  onCommit={(v) => commitIdleWindow('noSize', v)}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>
            </div>
            {canConfigureAssists && idleCustom && (
              <div className="nudge-bar">
                <button className="link" onClick={async () => { await setIdleWindows(null); showFlash('Saved'); }}>
                  Reset to the defaults (3 / 5 / 10 / 5)
                </button>
              </div>
            )}
          </div>
        </div>
        {/* CHECKLIST PROMPTS (0214) — whether a card's own checklist may move the card
            along, under one master switch with a checkbox per kind of move so more
            kinds can be added later without another switch. Admin-only like the
            controls above; the per-kind checkboxes show only while the master is on. */}
        <div className={`feature-row ${prompts.on ? 'is-on' : ''}`} data-reveal="checklist-prompts">
          <button
            className={`switch ${prompts.on ? 'on' : ''}`}
            role="switch"
            aria-checked={prompts.on}
            aria-label="Let a checklist move the card along"
            disabled={!canConfigureAssists || promptsBusy}
            onClick={() => void commitPrompts({ ...prompts, on: !prompts.on })}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Let a checklist move the card along</div>
            <p className="feature-what">
              {prompts.on
                ? 'A card’s own checklist can offer to move the card as its boxes are ticked. Pick which offers below — each is an offer the person confirms, never a silent move.'
                : 'A checklist never moves the card, and the board’s Definition of Done stands as the only gate on Done. Turn on to let a checklist offer to start and finish a card, and complete it on its own.'}
            </p>
            {/* The per-kind choices glide in and out with the master switch (R32), so
                turning the feature off folds them away rather than popping. */}
            <Expander open={prompts.on}>
              <div className="checks prompt-kinds">
                <label className="check prompt-kind">
                  <input
                    type="checkbox"
                    checked={prompts.onFirstTick}
                    disabled={!canConfigureAssists || promptsBusy}
                    onChange={(e) => void commitPrompts({ ...prompts, onFirstTick: e.target.checked })}
                  />
                  <span>When the first box is ticked on a to-do card, offer to move it to In&nbsp;Progress</span>
                </label>
                <label className="check prompt-kind">
                  <input
                    type="checkbox"
                    checked={prompts.onComplete}
                    disabled={!canConfigureAssists || promptsBusy}
                    onChange={(e) => void commitPrompts({ ...prompts, onComplete: e.target.checked })}
                  />
                  <span>When every box is ticked, offer to move the card to Done — and let a full checklist complete a card in place of the Definition of Done</span>
                </label>
              </div>
            </Expander>
          </div>
          <span className={`feature-state ${prompts.on ? 'on' : ''}`}>{prompts.on ? 'On' : 'Off'}</span>
        </div>
      </section>
      )}

      {/* ── FINANCE & PAYMENTS (#488) — every payment-approval control in one
          place, beside where payments are governed. Project-mode only: a
          continuous service has no money to govern (the standing gate). */}
      {tab === 'Finance & payments' && deliveryMode === 'project' && (
        <p className="set-intro">
          How this board governs money — who may change the contract value, when a payment may be
          approved ahead of the work, and the duties that must stay in separate hands. The money
          itself lives under <b>Finances</b> in the sidebar.
        </p>
      )}

      {tab === 'Finance & payments' && <h3 className="grp-heading">Payment Approvals</h3>}

      {/* SEPARATION OF DUTIES — a STATED rule, not a toggle: it is always enforced
          (payment-approval.ts — whoever accepted the work cannot authorise its
          payment, at any approval seat), with an admin-only emergency override
          that needs a reason and is kept as a control exception. So it is shown
          and made findable, never switched. R12 (a plain bordered note, no rail);
          R22 (governed-decision framing). */}
      {tab === 'Finance & payments' && deliveryMode === 'project' && (
      <section className="panel">
        <h2 data-reveal="sod">Separation of Duties</h2>
        <div className="sod-note">
          <svg className="sod-ico" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path d="M9 1.5 3 4v4.2c0 3.5 2.4 6.3 6 7.8 3.6-1.5 6-4.3 6-7.8V4L9 1.5Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="m6.3 8.7 1.9 1.9 3.5-3.7" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="sod-body">
            <p className="sod-rule">
              Whoever records work as <b>accepted</b> cannot <b>authorise its payment</b> — at any
              seat in the approval chain, including the executive approver.
              <span className="sod-always">Always on</span>
            </p>
            <p className="sod-sub">
              An admin can override in an emergency, but only with a reason, and every override is
              kept as a <b>control exception</b> on the line, the ledger and History.
            </p>
          </div>
        </div>
        <p className="hint sod-foot">
          Set who may authorise payments in{' '}
          <button className="link" onClick={() => drillTo('plan', 'pay-approvers')}>
            the Plan, under Payment Approvals
          </button>
          . Work is accepted in the invoice line it pays for.
        </p>
      </section>
      )}

      {/* Scope-change governance — a project concern, so shown only in project mode
          (the standing gate rule). Who may change the contract value, and whether a
          stand-in is allowed (documented) or blocked (strict SoD). #435 A. */}
      {tab === 'Finance & payments' && deliveryMode === 'project' && (
      <section className="panel">
        <h2 data-reveal="scope-approver">Changing the contract value</h2>
        <p className="hint">
          Changing an already-set contract value is a scope change — a recorded decision,
          gated to the board&rsquo;s designated approver, or a manager or admin standing in. It is
          never a general editor&rsquo;s to make.
        </p>
        <div className={`feature-row ${strictScopeApprover ? 'is-on' : ''}`}>
          <button
            className={`switch ${strictScopeApprover ? 'on' : ''}`}
            role="switch"
            aria-checked={strictScopeApprover}
            aria-label="Require a separate approver for scope changes"
            disabled={!canSettings}
            onClick={() => void setRequireSeparateScopeApprover(!strictScopeApprover)}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Require a separate approver for scope changes</div>
            <p className="feature-what">
              {strictScopeApprover
                ? 'Only the designated approver can change the contract value. A manager or admin standing in is blocked — set a scope approver if there is none.'
                : 'A manager or admin standing in can change the contract value — it is allowed, but recorded as a control exception. Turn this on to reserve scope approval to the designated approver.'}
            </p>
          </div>
          <span className={`feature-state ${strictScopeApprover ? 'on' : ''}`}>
            {strictScopeApprover ? 'On' : 'Off'}
          </span>
        </div>
      </section>
      )}

      {/* The delivery nudge — warn (never block) when a payment is approved while the
          work it pays for is below a chosen share delivered. #483. A project concern
          (delivery is measured against phases, epics or milestones), so project mode. */}
      {tab === 'Finance & payments' && deliveryMode === 'project' && (
      <section className="panel">
        <h2 data-reveal="delivery-nudge">Approving ahead of delivery</h2>
        <p className="hint">
          A payment pays for work that is meant to be delivered. FlightDeck can warn
          whoever approves a payment when the work it pays for is less than a set share
          delivered. It is a prompt to look first &mdash; never a block, and the approver
          can still go ahead. If they do, the shortfall is recorded on the line.
        </p>
        <div className={`feature-row ${nudgeOn ? 'is-on' : ''}`}>
          <button
            className={`switch ${nudgeOn ? 'on' : ''}`}
            role="switch"
            aria-checked={nudgeOn}
            aria-label="Warn when a payment is approved below its delivered share"
            disabled={!canSettings}
            onClick={() => void toggleNudge()}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Warn when a payment is approved ahead of delivery</div>
            <p className="feature-what">
              {nudgeOn
                ? <>Approving a payment whose work is below <b>{nudgePct}%</b> delivered shows a warning first, and records that it was approved below the bar.</>
                : <>No warning is shown, however little of the work has been delivered. Turn this on to set a delivery bar.</>}
            </p>
            {nudgeOn && (
              <div className="nudge-bar">
                <label className="nudge-bar-l" htmlFor="nudge-pct">Warn below</label>
                <div className="nudge-bar-field">
                  <input
                    id="nudge-pct"
                    className="input nudge-pct"
                    type="number"
                    min={1}
                    max={100}
                    inputMode="numeric"
                    value={nudgeDraft}
                    disabled={!canSettings}
                    aria-invalid={!nudgeDraftValid}
                    onChange={(e) => setNudgeDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && nudgeDirty) void saveNudgeBar(); }}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                  <span className="nudge-pct-sign">%</span>
                </div>
                {nudgeDirty && (
                  <button className="btn btn-primary btn-small" disabled={!canSettings} onClick={() => void saveNudgeBar()}>
                    Save bar
                  </button>
                )}
              </div>
            )}
            {nudgeErr && <p className="nudge-err">{nudgeErr}</p>}
          </div>
          <span className={`feature-state ${nudgeOn ? 'on' : ''}`}>
            {nudgeOn ? 'On' : 'Off'}
          </span>
        </div>
      </section>
      )}

      {/* CURRENCY & EXCHANGE RATES — the base (reporting) currency every budget
          rolls up to, and the rates foreign payments convert at. Tenant-wide
          (0117/0118), so it is the SAME editor and state the Workspace hub shows;
          surfaced here, beside the money controls, because this is where a person
          goes to govern money (#488). Admin-only to edit (the shared control gates
          itself and the server enforces it); everyone else sees the read-only
          summary. */}
      {tab === 'Finance & payments' && <h3 className="grp-heading">Currency &amp; Exchange Rates</h3>}

      {tab === 'Finance & payments' && (
      <section className="panel">
        <h2 data-reveal="currency">Currency &amp; Exchange Rates</h2>
        <p className="hint">
          The base currency every budget rolls up to, and the exchange rates foreign payments convert
          at. These apply to the <b>whole workspace</b>, not just this board.
        </p>
        <CurrencyRates />
      </section>
      )}

      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="cadence">How this board plans its work</h2>
        <p className="hint">
          Flow asks &ldquo;what is the single best thing to start now&rdquo;. Sprints ask
          &ldquo;what is left in this one&rdquo;. Both are real ways to work; pick the one your
          team already uses rather than the one that sounds more organised.
        </p>
        <div className="mode-cards">
          {(['builds', 'sprints'] as const).map((c) => (
            <button
              key={c}
              className={`mode-card ${cadence === c ? 'is-on' : ''}`}
              disabled={!canSettings}
              onClick={() => void setCadence(c)}
            >
              <span className="mode-card-title">
                {CADENCE_LABEL[c]}
                {cadence === c && <span className="person-you">in use</span>}
              </span>
              <span className="mode-card-blurb">{CADENCE_BLURB[c]}</span>
            </button>
          ))}
        </div>
        {/*
          * TURNING IT OFF LEAVES THE SPRINTS ALONE, said here rather than
          * discovered. Deleting them would throw away every record of what was
          * planned and when — which is exactly what a team turning it off to
          * try flow for a fortnight would want back if they change their mind.
          */}
        {cadence === 'sprints' && sprints.length > 0 && (
          <p className="hint">
            Switching back to flow hides the sprints rather than deleting them. Turn it on again
            and they are all still here.
          </p>
        )}
      </section>
      )}

      {/* The working calendar (0216) — a WORKSPACE-wide control shown here because it
          is what the cadence forecast reads, mirroring how Currency appears in the
          Finance tab. One store, one editor; the Workspace hub shows the same one. */}
      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="working-days">Working Days &amp; Holidays</h2>
        <p className="hint">
          The days your organisation works and the holidays it closes for. The cadence forecast and the
          stalled-card reminders count only working days, so a known close pushes a prediction out rather
          than assuming work carried on. This applies to the <b>whole workspace</b>, not just this board.
        </p>
        <WorkCalendarEditor />
      </section>
      )}

      {/* The project's shape over time, then the two classifications — a phase is
          when the work happens, an area is what it is about, a type is what sort
          it is. #377 / #302. The reveal anchor hangs from the group heading. */}
      {tab === 'Workflow' && <h3 className="grp-heading" data-reveal="classification">How work is classified</h3>}
      {tab === 'Workflow' && <PhaseSettings />}
      {tab === 'Workflow' && <AreaSettings />}
      {tab === 'Workflow' && <TypeSettings />}
      {tab === 'Workflow' && <EpicSettings />}
      {tab === 'Workflow' && <LabelSettings />}

      {/* ── RELEASES & DATA (#488) — what this board ships (versions, devices),
          then whole-board operations (export, archive). Absorbs the old Data tab
          and the release/version panel the old Assistants tab used to share. The
          two groups each carry a `.grp-heading` landmark so the tab reads as two
          named halves, matching the other tabs. */}
      {tab === 'Releases & data' && <h3 className="grp-heading">What this board ships</h3>}

      {tab === 'Releases & data' && (
      <section className="panel">
        <h2 data-reveal="version-numbering">Versions and Releases</h2>
        <p className="hint">
          A release is a version that went out, and what went out in it. It is recorded on the
          Releases screen whenever you ship — it does not have to line up with a sprint, though
          finishing one offers to record it.
        </p>
        {/*
          * VINNIE'S SWITCH, 15 August: "an option that allows an assistant driven
          * board to control the build and version number. Once the switch is off,
          * it works as you recommended."
          *
          * OFF BY DEFAULT and stated as a choice rather than a detection. There
          * is already a `keptByAssistant` flag that the pushing tool STAMPS, and
          * this could have read it — but that would mean a board silently
          * changing who is in charge of its version numbers because a tool was
          * pointed at it once. Who holds the pen is a decision, and decisions are
          * made by people.
          */}
        {/* The switch the rest of the app already uses — see AssistantSetup.
            A second kind of toggle on one settings screen is how an interface
            starts looking assembled rather than designed. */}
        <div className={`feature-row ${assistantVersions ? 'is-on' : ''}`}>
          <button
            className={`switch ${assistantVersions ? 'on' : ''}`}
            role="switch"
            aria-checked={assistantVersions}
            aria-label="Versions come from the assistant"
            disabled={!canSettings}
            onClick={() => void setAssistantVersions(!assistantVersions)}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">Versions come from the assistant</div>
            <p className="feature-what">
              {assistantVersions
                ? 'The tool that pushes to this board sets the version and the build number. FlightDeck records what it is told, so its number and the one in your code cannot disagree.'
                : 'You type the version when you record a release, and FlightDeck only suggests what might come next. Turn this on if a tool or an assistant already numbers your builds.'}
            </p>
          </div>
          <span className={`feature-state ${assistantVersions ? 'on' : ''}`}>
            {assistantVersions ? 'On' : 'Off'}
          </span>
        </div>
        {assistantVersions && builds.length > 0 && (
          <p className="hint">
            Last pushed: <strong>{lastRelease(builds)?.version || `build ${lastRelease(builds)?.build}`}</strong>
            {lastRelease(builds)?.date ? ` on ${lastRelease(builds)?.date}` : ''}. Turning this off
            hands the numbering back to you — nothing already recorded changes.
          </p>
        )}
        {/*
          * DOES THIS PROJECT SHIP TO ANYTHING? Off by default, which is the half
          * that matters — see migration 0024. Vinnie found this by reading his
          * own board: *"every release version says 'Phones not recorded'. This
          * app is not only meant for phones, but for desktop software as well…
          * But if I am developing a SaaS then there will be no recorded device."*
          *
          * MOVED HERE ON 15 AUGUST, out of the Project panel below. Vinnie: *"the
          * 'This project ships to devices' should be above the Project name as
          * this is part the versions and releases section as they're pretty much
          * coinciding."* He is right, and the reason is stronger than tidiness:
          * the two switches answer the same question from two sides — what a
          * release IS on this project — and the setting only ever matters at the
          * moment somebody records one. Sat under the repository URL it read as
          * project admin, which is how it came to be turned on for one board
          * while the releases being looked at were on another.
          *
          * The same switch component as the assistant one, deliberately. A
          * second kind of toggle on one settings screen is how an interface
          * starts looking assembled rather than designed.
          */}
        <div className={`feature-row ${tracksDevices ? 'is-on' : ''}`} data-reveal="ships-to-devices">
          <button
            className={`switch ${tracksDevices ? 'on' : ''}`}
            role="switch"
            aria-checked={tracksDevices}
            aria-label="This project ships to devices"
            disabled={!canSettings}
            onClick={() => setMeta({ tracksDevices: !tracksDevices })}
          >
            <span className="switch-knob" />
          </button>
          <div className="feature-main">
            <div className="feature-name">This project ships to devices</div>
            <p className="feature-what">
              {tracksDevices
                ? 'Each release can name what it was installed on — phones, laptops, test machines, whatever it reaches. The names you type are offered again next time.'
                : 'Nothing is installed anywhere: a website or a hosted service is released, not installed. Releases say nothing about devices, which is the right answer rather than a blank.'}
            </p>
          </div>
          <span className={`feature-state ${tracksDevices ? 'on' : ''}`}>
            {tracksDevices ? 'On' : 'Off'}
          </span>
        </div>
        {/* Turning it off HIDES rather than deletes, and saying so is the
            difference between a switch somebody will try and one they will not.
            Nothing about a release is edited by this. */}
        {!tracksDevices && everRecorded.length > 0 && (
          <p className="hint">
            {everRecorded.length === 1
              ? `One device has been recorded on this project — ${everRecorded[0]}. It is kept, not deleted, and comes back if you turn this on.`
              : `${everRecorded.length} devices have been recorded on this project — ${everRecorded.slice(0, 4).join(', ')}${everRecorded.length > 4 ? ' and others' : ''}. They are kept, not deleted, and come back if you turn this on.`}
          </p>
        )}
        {/* WHOSE SOFTWARE THIS IS — the copyright line, in the one Settings
            section that is about the product itself rather than this board. Same
            string as the sign-in foot (../brand), so the two never disagree. */}
        <p className="hint settings-copyright">{COPYRIGHT}</p>
      </section>
      )}

      {tab === 'Releases & data' && <h3 className="grp-heading">Backup &amp; Lifecycle</h3>}

      {tab === 'Releases & data' && (
      <section className="panel">
        <h2>Data</h2>
        <p className="hint">
          This board is shared live between everyone with access — a change one person makes
          appears for everyone else without anybody reloading. Who can open it is controlled under
          People and access.
        </p>
        <button className="btn" data-reveal="export" onClick={exportJson}>
          Export backup (JSON)
        </button>
      </section>
      )}

      {/*
        * PUTTING A BOARD AWAY — #327, and it is archive rather than delete.
        *
        * Vinnie, 1 September 2026: *"there is no way to delete a board from
        * within the app even though I am an admin."* True — and the rules had
        * always allowed it, `boards_delete` grants it to an admin or the
        * board's owner. What was missing was the decision, not the button.
        *
        * The answer is the same one areas and types already got: Remove means
        * retire. A board carries issues, comments, activity, releases and
        * sign-offs, and a sign-off is a record somebody put their name to.
        * Nothing on this screen should be able to take that away on one click,
        * so nothing here does.
        *
        * LAST ON THE TAB ON PURPOSE. Everything above changes what the board
        * is; this is the only control that decides whether it is anywhere at
        * all, and it should not sit next to the name box somebody is editing.
        */}
      {tab === 'Releases & data' && canSettings && (
      <section className="panel">
        <h2 data-reveal="archive">Putting this board away</h2>
        <p className="hint">
          A board you have finished with, or made by mistake, can be put away. It leaves the
          board list and nobody is offered it &mdash; but nothing is deleted. Its issues,
          comments, releases and sign&#8209;offs stay exactly as they are, and bringing it back
          puts it where it was.
        </p>

        {!archiving ? (
          <div className="btn-row">
            <button className="btn btn-danger" onClick={() => { setArchiveSays(''); setArchiving(true); }}>
              Put {meta.name} away
            </button>
          </div>
        ) : (
          <div className="grid-warning">
            <p>
              <strong>Put {meta.name} away?</strong> It stops being offered to everyone on it
              &mdash; {members.length === 1 ? '1 person' : `${members.length} people`} &mdash; and
              the {issues.length === 1 ? '1 issue' : `${issues.length} issues`} on it go with it.
              You can bring it back from this screen on any of your other boards.
            </p>
            <div className="btn-row">
              <button
                className="btn btn-danger"
                onClick={() =>
                  void archiveProject(projectId, true).then((said) => {
                    setArchiveSays(said);
                    if (!said) setArchiving(false);
                  })
                }
              >
                Yes, put it away
              </button>
              <button className="btn" onClick={() => { setArchiving(false); setArchiveSays(''); }}>
                Keep it
              </button>
            </div>
          </div>
        )}

        {/* The refusal is SHOWN rather than the button being greyed out: a
            control that does nothing and says nothing is how somebody decides
            the feature is broken. See archiveProject for the one refusal. */}
        {archiveSays && <p className="login-error">{archiveSays}</p>}

        {/* The same shape as the retired areas above it, and for the same
            reason: this IS that list, for boards. Somebody who has put an area
            back already knows how this works. */}
        {archivedProjects.length > 0 && (
          <>
            <h3 className="area-heading">Put away</h3>
            <ul className="area-list">
              {archivedProjects.map((p) => (
                <li key={p.id} className="area-row is-retired">
                  <div className="area-line">
                    <span className="area-name-flat">{p.name}</span>
                    <span className="area-used">{p.key}</span>
                    <button
                      className="btn btn-small"
                      onClick={() => void archiveProject(p.id, false).then(setArchiveSays)}
                    >
                      Bring it back
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Said only when it is the reason the button above will refuse, so it
            is read at the moment it matters rather than ignored as boilerplate. */}
        {projects.length <= 1 && (
          <p className="hint">
            This is your only board, so it cannot be put away &mdash; there would be nothing left
            to open, and the app would offer to set a new workspace up.
          </p>
        )}
      </section>
      )}


      {tab === 'Sign-off' && (
      <section className="panel">
        <h2 data-reveal="signoff">Sign-Off</h2>
        <p className="hint">
          Who signs this project&rsquo;s work off — on a finished sprint, a release or a document.
          Set once here rather than typed each time.{' '}
          {stagesOn
            ? 'Set the stages work is approved in, and who signs each, in order.'
            : slotsOn
              ? 'Name the people who sign and tag each one to a slot below — the developer, the tester(s), the approver and the owner.'
              : 'Below the names, ranks can be asked as well, and those are worked out fresh every time approval is asked for.'}
        </p>
        {/* WHO APPROVES, TYPED RATHER THAN PICKED. The approver is often somebody
            who will never open FlightDeck — a client's sponsor — so this is a
            name and not an account. See #201 for the decision and #213 for the
            version that draws on a company directory instead.

            NAME AND POSITION ARE ASKED SEPARATELY. A real sign-off sheet has a
            Title column and a Name column, and one live example reads
            "Marketing, Customer Experience and Shared Services Manager" — so
            there is no separator that could have split one field safely. The
            position also outlasts the person: an approver moves on and their
            successor inherits the role, not the name. */}
        {/*
          ── FOUR THINGS, AND THEY NOW LOOK LIKE FOUR — #355 ──────────────────
          Vinnie, 4 September 2026: *"Why does this whole section look messy?
          Aren't these different sections?"* They were: the owner, when the work
          was asked for, the people named by hand, and the rule. All four were
          captioned in the same small grey text a hint is set in, one under
          another with the same gap between them as within them — so a screen
          asking four separate questions read as one long form.

          `.grp-heading` rather than anything new: the app has had one treatment
          for a section heading since #345, and the rule below this already uses
          it for its own three blocks.
        */}
        <div className="ap-sec">
          <h3 className="grp-heading">
            The project owner
            <InfoTip label="the project owner">
              The owner is the first name on every sign-off sheet and, unless you remove them below,
              signs the project&rsquo;s work off. Give them an email address and FlightDeck sends them
              their own signing link; leave it blank and their answer is recorded on their behalf,
              with the trail showing who pressed it.
            </InfoTip>
          </h3>
          <span className="hint">The person this project is being built for, who signs its work off.</span>
          <div className="owner-row">
            {/*
              ⚠️ ALWAYS DRAWN, AND IT USED NOT TO BE — #354. It appeared with the
              first letter typed, which moved both fields sideways under the
              hands of whoever was typing, and left the email row below sitting
              where it had been: two rows that belong to one person, no longer
              lining up with each other.

              It was absent on purpose, and the reasoning was that an empty
              circle looks like a person whose name failed to load. That is
              true of an EMPTY one. A question mark is not empty — it says the
              name has not been given yet, which is the actual state — so the
              row can hold still and still not lie.
            */}
            <span
              className={`avatar-me owner-av ${(meta.projectOwnerName ?? '').trim() ? '' : 'is-unset'}`}
              aria-hidden="true"
            >
              {(meta.projectOwnerName ?? '').trim() ? initials(meta.projectOwnerName ?? '') : '?'}
            </span>
            <MetaText
              className="input"
              aria-label="Project owner name"
              value={meta.projectOwnerName ?? ''}
              disabled={!canSettings}
              placeholder="Name — e.g. Pat Moore"
              onCommit={(v) => setMeta({ projectOwnerName: v })}
            />
            <MetaText
              className="input"
              aria-label="Project owner position"
              value={meta.projectOwnerPosition ?? ''}
              disabled={!canSettings}
              placeholder="Position — e.g. Finance Director"
              onCommit={(v) => setMeta({ projectOwnerPosition: v })}
            />
          </div>
          {/* THEIR EMAIL ADDRESS, WHICH NOW DOES TWO JOBS — and used to say only
              one of them. It was labelled "their FlightDeck account", which was
              true until #216: it decided whether they answered their own row in
              the app or had somebody record it for them.

              Since FlightDeck posts the signing link, this is ALSO where that
              link goes. And the people this feature exists for — a client's
              sponsor, an executive director — have no FlightDeck account, so a
              field asking for one is a field they correctly leave blank, and
              then no link is ever sent. Asking for an address rather than an
              account is the same question with the answer people can give. */}
          {/* INDENTED PAST THE AVATAR, so the two rows this person occupies line
              up as one block rather than as two things that happen to be near
              each other. */}
          <MetaText
            className="input owner-email"
            type="email"
            aria-label="Project owner email address"
            value={meta.projectOwnerEmail ?? ''}
            disabled={!canSettings}
            placeholder="Their email address — so the link is sent to them"
            onCommit={(v) => setMeta({ projectOwnerEmail: v })}
          />
          <span className="hint">
            A name, not an account — they do not need to be on the board. Naming them puts them
            at the top of the panel below. Give an address and FlightDeck emails them their own
            signing link, which they answer themselves; leave it blank and work is signed off on
            their behalf, with the trail saying who pressed it.
          </span>
        </div>

        {/* WHEN IT WAS ASKED FOR, which is not when it started. The gap between
            the two is the only one of the three dates nobody can reconstruct
            afterwards, and it is the first thing asked when somebody wants to
            know whether a request was acted on. */}
        {/*
          ⚠️ A DIV WRAPPING A LABEL, NOT A LABEL WRAPPING EVERYTHING — #353.

          Vinnie, 4 September 2026: *"the date field clickable button is the
          entire row across the screen, so clicking all the way to the right of
          the window, outside of the date field will open the calendar"*. A
          `<label>` is a click target for the whole of its own box, and these
          run the width of the panel — so most of the target for a 210px field
          was empty space nowhere near it. On a text field that only moves the
          cursor; on a date field it opens a calendar over the page.

          The caption keeps its label, so the field still has a name a screen
          reader reads and clicking the words still reaches it. It just stops at
          the end of the words.
        */}
        <div className="ap-sec">
          <h3 className="grp-heading">
            When the work was asked for
            <InfoTip label="when the work was asked for">
              The day the work was first requested &mdash; not the day anyone started it. FlightDeck
              already stamps when approval was asked for and when each person signs; this is the one
              date it cannot work out for you later. The gap between it and the signatures is how long
              a request sat before it was acted on.
            </InfoTip>
          </h3>
          <label className="stack-lbl" htmlFor="requested-on">
            Date requested{' '}
            <span className="hint">(not when it started)</span>
          </label>
          {/* The typed date field parses what a person writes and only ever
              hands back a whole valid day, so the old two-digit-year mangling
              (Vinnie's "08/10/0026") and its draft-on-blur workaround are gone. */}
          <DateField
            id="requested-on"
            ariaLabel="Date requested"
            value={meta.requestedOn ?? ''}
            onChange={(v) => setMeta({ requestedOn: v })}
            disabled={!canSettings}
            variant="form"
          />
        </div>

        {/* WHO SIGNS, SET ONCE. The same roles sign every release, so asking
            again each time is retyping — and retyping is where a row goes
            missing. A missed row is a missed approval nobody notices, because
            the sheet still looks complete.

            THIS IS THE DEFAULT, NOT A LIVE LINK. A release freezes its own
            signatories when approval is requested, so editing this never
            alters something already part-signed.

            NOT ON THE STAGE MODEL. Stages name their own signers — a role, a
            person on the board, or an outside person — so this hand-typed panel
            (which the rank and slots models used) is hidden there to keep one
            way of saying who signs on screen at a time. */}
        {!stagesOn && (
        <div className="ap-sec sig-panel">
          <h3 className="grp-heading">
            Signatories named by hand
            <InfoTip label="signatories named by hand">
              The people who sign this project&rsquo;s work off, set once here so nobody is retyped
              each time approval is asked for. An email address lets someone sign their own row; leave
              it blank and their answer is recorded on their behalf. On a board that approves in
              order, tag each person to the step they answer.
            </InfoTip>
          </h3>
          <span className="hint">
            {slotsOn
              ? 'The people the slots below ask for — tag each one to the slot they fill.'
              : 'Asked every time, whatever ranks the rule below asks as well.'}
          </span>
          <span className="hint">
            An account is optional. With one, that person signs their own row themselves,
            whatever they may do elsewhere on the board. Without one, their answer is recorded
            on their behalf and the sheet says who did it.
          </span>
          {/* ROW ONE, AND NOT TYPED IN TWICE. The owner commissioned the work,
              so they approve it — see #201 and 0053. Shown rather than editable
              because their name is the field above: two boxes for one name is
              how a panel ends up disagreeing with the board about who the owner
              is. Remove records that they do not sign off; it does not forget
              them. */}
          {ownerNamed && meta.ownerSignsOff !== false && (
            <div className="owner-row sig-owner">
              <span className="sig-fixed">
                {meta.projectOwnerName}
                <span className="sig-tag">Project owner</span>
              </span>
              <span className="sig-fixed sig-fixed-role">{meta.projectOwnerPosition}</span>
              <span className="sig-fixed sig-account">
                {(meta.projectOwnerEmail ?? '').trim() || 'no account'}
              </span>
              <button
                className="btn btn-small sig-drop"
                disabled={!canSettings}
                aria-label={`Remove ${meta.projectOwnerName} from the panel`}
                onClick={() => setMeta({ ownerSignsOff: false })}
              >
                Remove
              </button>
            </div>
          )}
          {ownerNamed && meta.ownerSignsOff === false && (
            <p className="hint sig-owner-off">
              {meta.projectOwnerName} owns this project but is not asked to approve anything.{' '}
              {canSettings && (
                <button className="link" onClick={() => setMeta({ ownerSignsOff: true })}>
                  Put them back on the panel
                </button>
              )}
            </p>
          )}
          {(meta.signatories ?? []).map((who, at) => (
            <div className="owner-row" key={at}>
              <MetaText
                className="input"
                aria-label={`Signatory ${at + 1} name`}
                value={who.name ?? ''}
                disabled={!canSettings}
                placeholder="Name"
                onCommit={(v) => setSignatory(at, { ...who, name: v })}
              />
              <MetaText
                className="input"
                aria-label={`Signatory ${at + 1} position`}
                value={who.position ?? ''}
                disabled={!canSettings}
                placeholder="Position"
                onCommit={(v) => setSignatory(at, { ...who, position: v })}
              />
              {/* OPTIONAL, AND THE DIFFERENCE IT MAKES IS WHOSE SIGNATURE IT
                  IS. Left blank the row is answered on their behalf, stamped
                  with who and why. Given an address, FlightDeck sends them
                  their own signing link and they answer it themselves at any
                  rank — being named is the permission. It said "Account", which
                  read as "a FlightDeck login" and was left blank for exactly
                  the outside approvers this is for. See the owner's field. */}
              <MetaText
                className="input sig-account"
                type="email"
                aria-label={`Signatory ${at + 1} email address`}
                value={who.email ?? ''}
                disabled={!canSettings}
                placeholder="Email address"
                onCommit={(v) => setSignatory(at, { ...who, email: v })}
              />
              {/*
                ── WHICH STEP THIS PERSON ANSWERS — #352 ─────────────────────
                Vinnie, 4 September 2026, reading the chain on screen: *"where
                is the tester themselves?"* A step could only be given RANKS,
                and a tester is not a rank in this product — so the step named
                after the tester was the one step that could not ask for one.

                ⚠️ ONLY ON A BOARD THAT APPROVES IN ORDER. With one approval
                there is nothing to choose between, and a selector offering one
                answer is a question that should not have been asked. Every
                board today is that board.
              */}
              {chain.length > 0 && (
                <select
                  className="input sig-step"
                  aria-label={`Which step ${who.name || `signatory ${at + 1}`} answers`}
                  value={who.step ?? ''}
                  disabled={!canSettings}
                  onChange={(e) =>
                    setSignatory(at, { ...who, step: e.target.value as SignoffStep })
                  }
                >
                  {/* NOT A DEFAULT, AND DELIBERATELY NOT ONE. [[#346]] put every
                      typed name on the last step and that guess was wrong for
                      the commonest case there is. An unanswered question says
                      so, here and in the rule below. */}
                  <option value="">Not asked yet</option>
                  {chain.map((s) => (
                    <option value={s} key={s}>
                      {stepLabel(s)}
                    </option>
                  ))}
                </select>
              )}
              <button
                className="btn btn-small sig-drop"
                disabled={!canSettings}
                aria-label={`Remove ${who.name || 'this signatory'} from the panel`}
                onClick={() => setSignatory(at, null)}
              >
                Remove
              </button>
            </div>
          ))}
          {canSettings && (
            <button
              className="btn btn-small sig-add"
              onClick={() => setMeta({ signatories: [...(meta.signatories ?? []), { name: '', position: '' }] })}
            >
              Add a signatory
            </button>
          )}
          {/* REMEMBERED, NOT RESURRECTED. Somebody taken off the panel is still
              somebody this project knows, so they come back as a press rather
              than as a name typed again slightly differently — two spellings of
              one person is two people to any record. */}
          {canSettings && offerBack(meta).length > 0 && (
            <div className="sig-known">
              <span className="hint">Signed here before:</span>
              {offerBack(meta).map((who) => (
                <button
                  key={`${who.name}|${who.position}`}
                  className="chip on sig-chip"
                  onClick={() => setMeta({ signatories: [...(meta.signatories ?? []), who] })}
                >
                  {who.name} <span className="sig-chip-role">{who.position}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* THE RULE SITS UNDER THE NAMES, not instead of them — #332. On the
            legacy models the panel above is who is typed in and this is which
            ranks are asked as well; on the stage model this IS the whole editor,
            and the panel above is hidden. */}
        <SignoffRule canSettings={canSettings} />
      </section>
      )}
      {tab === 'Workflow' && <h3 className="grp-heading">What finishing means</h3>}
      {tab === 'Workflow' && (
      <section className="panel">
        <h2 data-reveal="done-checks">What &ldquo;done&rdquo; means here</h2>
        <p className="hint">
          Nothing on this project can be moved to Done until every one of these is ticked. One list
          for the whole project, so &ldquo;done&rdquo; means the same thing on every issue.
        </p>
        <label className="stack">
          One per line <span className="hint">(saves itself; leave empty for no checks)</span>
          <textarea
            className="input desc"
            rows={4}
            disabled={!canSettings}
            placeholder={SUGGESTED_CHECKS.join('\n')}
            value={checksDraft}
            onFocus={() => (editingChecks.current = true)}
            onChange={(e) => queueChecksSave(e.target.value)}
          />
        </label>
        {(meta.doneChecks ?? []).length === 0 && canSettings && (
          <button
            className="btn btn-small"
            onClick={() => {
              setChecksDraft(SUGGESTED_CHECKS.join('\n'));
              setMeta({ doneChecks: SUGGESTED_CHECKS });
            }}
          >
            Use a sensible starting list
          </button>
        )}
        <p className="hint">
          Changing this does not disturb work already finished — an issue keeps a record of what it
          was actually held to at the time.
        </p>
      </section>
      )}

      {/* Always present within its tab. It is not a control that can do nothing
          — it is the path to making it do something, which is a different thing. */}
      {tab === 'Assistants' && <AssistantSetup />}

      {importing && <Import close={() => setImporting(false)} />}
    </div>
  );
}

/**
 * THE LOGO ON THIS BOARD'S DOCUMENTS — #195, migration 0063.
 *
 * Vinnie, 23 August 2026: *"we should be able to upload a company logo so that
 * it becomes a branded form like everything else is supposed to be."*
 *
 * SHOWN AS IT WILL BE USED. A logo box that says "logo.png" tells somebody the
 * upload worked and nothing about whether the thing they picked is the right
 * shape, the right crop, or legible small — which is the whole question. It is
 * drawn at the height it gets on the document.
 */
function BoardLogo() {
  const meta = useApp((s) => s.meta);
  const upload = useApp((s) => s.uploadProjectLogo);
  const addressFor = useApp((s) => s.projectLogoUrl);
  const setMeta = useApp((s) => s.setMeta);
  const may = can(useApp((s) => s.shownRole), 'editSettings');

  const [address, setAddress] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let stopped = false;
    void addressFor().then((a) => {
      if (!stopped) setAddress(a);
    });
    return () => {
      stopped = true;
    };
  }, [addressFor, meta.logoPath]);

  return (
    <div className="logo-set">
      {meta.logoPath && address ? (
        <img className="logo-shown" src={address} alt="The logo on this board's documents" />
      ) : (
        <p className="hint logo-none">No logo &mdash; documents are produced unbranded.</p>
      )}

      {may && (
        <div className="doc-act">
          <label className="btn btn-small logo-pick">
            {busy ? 'Uploading…' : meta.logoPath ? 'Replace it' : 'Upload a logo'}
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="sr-only"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                setBusy(true);
                setProblem(await upload(file));
                setBusy(false);
              }}
            />
          </label>
          {meta.logoPath && (
            <button
              className="link"
              onClick={() => {
                /* The file is left where it is. Storage is cheap and a delete
                   that races a document being produced is not. */
                setMeta({ logoPath: '', logoType: '' });
              }}
            >
              Remove it
            </button>
          )}
          <span className="doc-hint">PNG or JPEG, under 2MB.</span>
        </div>
      )}
      {problem && <p className="problem">{problem}</p>}
    </div>
  );
}
